// Required for Dawn product media switching.
// Must load after global.js

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');

        // Viewer is required for switching; thumbnails are optional.
        if (!this.elements.viewer) return;

        // Guard: debounce must exist (global.js should define it)
        const safeDebounce =
          typeof window.debounce === 'function'
            ? window.debounce
            : (fn, wait) => {
                let t;
                return (...args) => {
                  clearTimeout(t);
                  t = setTimeout(() => fn.apply(null, args), wait);
                };
              };

        this.elements.viewer.addEventListener(
          'slideChanged',
          safeDebounce(this.onSlideChanged.bind(this), 500)
        );

        // Wire thumbnail clicks if thumbnails exist
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            const btn = mediaToSwitch.querySelector('button');
            const target = mediaToSwitch.dataset.target;
            if (!btn || !target) return;

            btn.addEventListener('click', this.setActiveMedia.bind(this, target, false));
          });

          // Only run this on desktop thumbnail layouts
          if (
            typeof this.dataset.desktopLayout === 'string' &&
            this.dataset.desktopLayout.includes('thumbnail') &&
            this.mql.matches
          ) {
            this.removeListSemantic();
          }
        }
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;
        const mediaId = event?.detail?.currentElement?.dataset?.mediaId;
        if (!mediaId) return;

        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        if (!this.elements.viewer) return;

        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement?.prepend?.(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail?.parentElement?.prepend) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          // Optional viewer slider reset
          if (this.elements.viewer.slider && typeof this.elements.viewer.resetPages === 'function') {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          // If viewer supports scroll, try to align active media in the strip
          if (activeMedia.parentElement && typeof activeMedia.parentElement.scrollTo === 'function') {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }

          // Stacked layout or no thumbnails: scroll active media into view
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);

        const position = activeThumbnail?.dataset?.mediaPosition;
        if (position) this.announceLiveRegion(activeMedia, position);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails.querySelectorAll('button').forEach((element) => {
          element.removeAttribute('aria-current');
        });

        const btn = thumbnail.querySelector('button');
        if (btn) btn.setAttribute('aria-current', true);

        // --- Safe visibility check (supports Dawn slider-component OR plain list) ---
        const thumbs = this.elements.thumbnails;

        // If thumbnails is a slider-component that implements isSlideVisible(index, slider), use it
        if (typeof thumbs.isSlideVisible === 'function') {
          // Try to compute index
          const allTargets = Array.from(thumbs.querySelectorAll('[data-target]'));
          const idx = allTargets.indexOf(thumbnail);
          if (idx >= 0 && thumbs.isSlideVisible(idx, thumbs.slider || thumbs.querySelector('ul,ol'))) return;
        }

        // Fallback: check visibility by DOM rects
        const sliderEl =
          thumbs.slider ||
          thumbs.querySelector("[id^='Slider-']") ||
          thumbs.querySelector('[data-slider]') ||
          thumbs.querySelector('ul') ||
          thumbs.querySelector('ol');

        if (!sliderEl) return;

        const viewport = sliderEl.getBoundingClientRect();
        const rect = thumbnail.getBoundingClientRect();
        const tol = 1;
        const visible = rect.left >= viewport.left - tol && rect.right <= viewport.right + tol;

        if (visible) return;

        // Scroll thumbnail into view
        if (typeof sliderEl.scrollTo === 'function') {
          sliderEl.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
        } else {
          thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      }

      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion) return;

        const image = activeItem?.querySelector?.('.product__modal-opener--image img');
        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);

          const str = window.accessibilityStrings?.imageAvailable;
          if (typeof str === 'string') {
            this.elements.liveRegion.innerHTML = str.replace('[index]', position);
          }

          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        // re-trigger load handler
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        try {
          if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
        } catch (e) {}

        const deferredMedia = activeItem?.querySelector?.('.deferred-media');
        if (deferredMedia && typeof deferredMedia.loadContent === 'function') {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        // This method MUST NOT crash if slider pieces are missing.
        // Dawn sometimes uses viewer.slider and viewer.sliderItems, but customized setups may not.
        const viewer = this.elements.viewer;
        if (!viewer) return;

        const slider = viewer.slider || viewer.querySelector("[id^='Slider-']") || viewer.querySelector('ul,ol');
        if (!slider) return;

        slider.setAttribute('role', 'presentation');

        // Try common sources of "slides"
        const slides =
          viewer.sliderItems ||
          (viewer.slider && Array.isArray(viewer.slider.items) ? viewer.slider.items : null) ||
          Array.from(slider.children || []);

        if (!slides || !slides.forEach) return;

        slides.forEach((slide) => {
          if (slide && slide.setAttribute) slide.setAttribute('role', 'presentation');
        });
      }
    }
  );
}
